import { useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { RegionSetStatusType } from "@app/projects/enumerators";
import { MultiMapExportCounty, MultiMapExportStateGroup } from "@app/projects/network-assessment/multi-map-export/interfaces";
import ProjectService from "@app/projects/projectService";

export interface MultiMapExportServiceAreas {
  isError: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  states: readonly MultiMapExportStateGroup[];
}

const EMPTY_STATES: readonly MultiMapExportStateGroup[] = Object.freeze([]);

/**
 * A sibling route of the county map, so there is no FilterContext to read this from; mirrors
 * FilterContext.getServiceAreas instead.
 */
export const useMultiMapExportServiceAreas = (
  projectId?: number,
  projectRunConfigurationId?: number,
): MultiMapExportServiceAreas => {
  const projectService = useRef(new ProjectService()).current;

  const { data, isError, isLoading, isSuccess } = useQuery({
    enabled: !!projectId && !!projectRunConfigurationId,
    queryFn: async ({ signal }) => {
      const [statesWithCounties, statusRows] = await Promise.all([
        // useCanonicalRegionsSets must stay false: getAvailableStatuses keys off the project's own
        // region sets, so canonical region set ids would not join to it.
        projectService.getCountiesByRunConfigurationId(String(projectId), String(projectRunConfigurationId), false, true, signal),
        projectService.getAvailableStatuses(projectId, projectRunConfigurationId, signal),
      ]);

      // For a leaf region set the API returns a single-element set holding that county's own status.
      const statusByRegionSetId = new Map<number, RegionSetStatusType>(
        statusRows.map((row) => [row.regionSetId, row.availableStatuses?.[0]]),
      );

      return statesWithCounties
        .map((state) => ({
          counties: (state.children ?? [])
            .flatMap<MultiMapExportCounty>((county) => {
              const regionSetId = Number(county.key);
              const status = statusByRegionSetId.get(regionSetId);

              return !status || status === RegionSetStatusType.Unknown
                ? []
                : [{ countySsa: county.countySSA, name: county.name, regionSetId, status }];
            })
            .sort((first, second) => first.name.localeCompare(second.name)),
          // displayStateName: true swaps the fields — `key` holds the full state name ("Maryland"),
          // `name` the abbreviation ("MD"). See ProjectsController.GetCountiesByProjectRunConfigurationAsync.
          stateAbbreviation: state.name,
          stateName: state.key,
        }))
        .filter((state) => state.counties.length > 0)
        .sort((first, second) => first.stateName.localeCompare(second.stateName));
    },
    queryKey: ["multi-map-export", "service-areas", projectId, projectRunConfigurationId],
    staleTime: 5 * 60 * 1000,
  });

  return { isError, isLoading, isSuccess, states: data ?? EMPTY_STATES };
};
